import { useState, useEffect, useRef } from 'react'
import { DraftPick } from '../../Api/Draft/draft.types'
import { isDeepEqual } from '../../Utils'
import { ON_CLOCK_ANIMATION_DURATION } from '../../Constants/draft'

type AnimationState = 'entering' | 'exiting' | 'idle'

interface UseAnimatedPickResult {
  currentPick: DraftPick | null
  animationState: AnimationState
}

export const useAnimatedPick = (
  nextTeamToPick: DraftPick | null
): UseAnimatedPickResult => {
  const [currentPick, setCurrentPick] = useState<DraftPick | null>(null)
  const [animationState, setAnimationState] = useState<AnimationState>('idle')
  const isInitialLoad = useRef(true)
  const latestNextTeamToPick = useRef(nextTeamToPick)
  let exitTimer: NodeJS.Timeout | null = null
  let enterTimer: NodeJS.Timeout | null = null

  const cleanup = () => {
    if (exitTimer) clearTimeout(exitTimer)
    if (enterTimer) clearTimeout(enterTimer)
  }

  useEffect(() => {
    latestNextTeamToPick.current = nextTeamToPick
  }, [nextTeamToPick])

  useEffect(() => {
    const incomingPick = latestNextTeamToPick.current
    if (isDeepEqual(incomingPick, currentPick)) {
      return
    }

    if (isInitialLoad.current && incomingPick) {
      isInitialLoad.current = false
      setCurrentPick(incomingPick)
      setAnimationState('entering')
      const timer = setTimeout(() => {
        setAnimationState('idle')
      }, ON_CLOCK_ANIMATION_DURATION * 1000)
      return () => clearTimeout(timer)
    }

    if (currentPick) {
      setAnimationState('exiting')
      exitTimer = setTimeout(() => {
        setCurrentPick(incomingPick)
        if (incomingPick) {
          setAnimationState('entering')
          enterTimer = setTimeout(() => {
            setAnimationState('idle')
          }, ON_CLOCK_ANIMATION_DURATION * 1000)
        } else {
          setAnimationState('idle')
        }
      }, ON_CLOCK_ANIMATION_DURATION * 1000)
    } else if (incomingPick) {
      setCurrentPick(incomingPick)
      setAnimationState('entering')
      enterTimer = setTimeout(() => {
        setAnimationState('idle')
      }, ON_CLOCK_ANIMATION_DURATION * 1000)
    }

    return cleanup
  }, [nextTeamToPick])

  return { currentPick, animationState }
}
